import { ArrowBack, Home, Info, ZoomIn, ZoomOut } from '@mui/icons-material'
import { IconButton, Theme } from '@mui/material'
import { orange } from '@mui/material/colors'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import { percent } from 'csx'
import { observer } from 'mobx-react-lite'
import { Collection, View } from 'ol'
import Map from 'ol/Map'
import { Coordinate } from 'ol/coordinate'
import { Extent, getCenter } from 'ol/extent'
import Geometry from 'ol/geom/Geometry'
import BaseLayer from 'ol/layer/Base'
import LayerGroup from 'ol/layer/Group'
import ImageLayer from 'ol/layer/Image'
import VectorLayer from 'ol/layer/Vector'
import Projection from 'ol/proj/Projection'
import Static from 'ol/source/ImageStatic'
import VectorSource from 'ol/source/Vector'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { FullscreenSpinner } from '../../components/feedback/circular'
import { IDocumentRenderer, OcrImageData } from './IDocumentRenderer'
// import { KeyboardShortcutDialog } from './KeyboardShortcutDialog'
import { action, runInAction } from 'mobx'
import { usePrompts } from '../../services/notifications/PromptService'
import { KeyboardShortcutDialog } from './KeyboardShortcutDialog'
import { createBoundingBoxFeature, createExtent, createLayer } from './Layers'
import { useOcrContext } from './OcrContext'
import { BoundingBox, DocumentFormat, isBoundingBoxEmpty } from './OcrTypes'

export type FileMapLayers = {
	boundingBoxes: VectorLayer<VectorSource<Geometry>>
	additionalLayers: BaseLayer[]
}

type MapAssets = {
	map: Map
	layers: FileMapLayers
	htmlElement: HTMLDivElement
}

type FileMapProps = {
	hideBackButton?: boolean
}

export const FileMap = observer((props: FileMapProps) => {
	const { controller } = useOcrContext()
	const styles = useStyles()
	const history = useHistory()

	const [layers, setLayers] = useState<FileMapLayers>()
	const [documentRenderer, setDocumentRenderer] = useState<
		IDocumentRenderer | undefined
	>(controller.documentRenderer)

	useEffect(() => {
		setDocumentRenderer(controller.documentRenderer)
	}, [controller, controller.documentRenderer])

	const promptService = usePrompts()

	const mapRefs = useRef<MapAssets[]>([])

	console.log('minor re-render')

	/**
	 * there is a small flash that pops up when the zoom swaps before
	 * the page switches, to fix that you have to either queue the zoom (not easy)
	 * or halt the zoom until before the page swap. It likely involves stopping
	 * a few re-renders since a page swap involves three, but only needs one
	 *
	 * Those changes are in selectedField and currentPageNumber, perhaps cache selected field
	 * if it appears on a different page unless current page number changes? - would need a
	 * local copy of the observables for that
	 */

	const imageData = useMemo<OcrImageData | undefined>(() => {
		const documentRenderer = controller.documentRenderer

		if (documentRenderer === undefined) return undefined

		const imageData = documentRenderer.getPage(controller.currentPageNumber)
		return imageData
	}, [controller.documentRenderer, controller.currentPageNumber])

	const rendered = useMemo(() => {
		return imageData !== undefined ? imageData.rendered : false
	}, [imageData, imageData?.rendered])

	const imageExtent = useMemo(() => {
		return imageData !== undefined && imageData !== undefined
			? imageData.extent
			: undefined
	}, [imageData, imageData?.extent])

	useEffect(() => {
		console.log('setting layers !')
		setLayers(getLayers())
	}, [controller.documentRenderer, controller])

	const mapController = useMemo(
		() => controller.mapControl,
		[controller.mapControl],
	)

	useEffect(() => {
		if (mapController === undefined || mapController.fitToPage === undefined)
			return
		console.log(' fitting to page')
		mapController.fitToPage(controller.currentPageNumber)
	}, [mapController])

	const getLayers = () => {
		/** this is here because the earlier call is asynchronous, therefor we run the risk
		 * of requesting something from a promise that hasn't completed yet */

		if (controller.documentRenderer === undefined) return

		console.log('past guard')

		const boundingBoxLayer = createLayer()

		return {
			boundingBoxes: boundingBoxLayer,
			additionalLayers: [],
		} as FileMapLayers
	}

	if (!rendered) return <FullscreenSpinner />

	if (layers === undefined) {
		console.log('layers is undefined')
		return <FullscreenSpinner />
	}
	if (
		imageExtent === undefined ||
		(imageExtent[2] === 0 && imageExtent[3] === 0)
	) {
		console.log('image extent undefined')
		return <FullscreenSpinner />
	}
	if (controller.ocrExtent === undefined) {
		console.log('ocr extent undefined')
		return <FullscreenSpinner />
	}

	mapController.zoomIn = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		map.getView().setZoom(map.getView().getZoom()! + 0.3)
	}

	mapController.zoomOut = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		map.getView().setZoom(map.getView().getZoom()! - 0.3)
	}

	mapController.panUp = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		const center = map.getView().getCenter()!
		const coordinate: Coordinate = [center[0], center[1] + 50]
		map.getView().setCenter(coordinate)
	}

	mapController.panDown = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		const center = map.getView().getCenter()!
		const coordinate: Coordinate = [center[0], center[1] - 50]
		map.getView().setCenter(coordinate)
	}

	mapController.panLeft = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		const center = map.getView().getCenter()!
		const coordinate: Coordinate = [center[0] - 50, center[1]]
		map.getView().setCenter(coordinate)
	}

	mapController.panRight = () => {
		const { map } = mapRefs.current[controller.currentPageNumber]
		const center = map.getView().getCenter()!
		const coordinate: Coordinate = [center[0] + 50, center[1]]
		map.getView().setCenter(coordinate)
	}

	const resetZoom = (pageNumber: number) => {
		if (controller.documentRenderer === undefined) return

		const { map, htmlElement } = mapRefs.current[pageNumber]

		const currentImage = controller.documentRenderer.getPage(pageNumber)

		console.log(
			pageNumber,
			JSON.stringify(currentImage?.extent, null, 2),
			currentImage?.rendered,
		)

		if (currentImage === undefined) throw new Error('how even?')
		map.getView().setCenter(getCenter(currentImage.extent))
		map.getView().setZoom(getDefaultZoom(currentImage.extent, htmlElement))
	}

	mapController.fitToPage = action((pageNumber, clearBoxes = false) => {
		console.log('fitting to page')
		const oldPageNumber = controller.currentPageNumber
		if (pageNumber === undefined) pageNumber = controller.currentPageNumber

		controller.currentPageNumber = pageNumber

		if (oldPageNumber !== pageNumber) {
			resetZoom(oldPageNumber)
			const { layers } = mapRefs.current[oldPageNumber]
			layers.boundingBoxes.getSource()?.clear()
		}

		setTimeout(() => {
			if (pageNumber === undefined) return
			resetZoom(pageNumber)
			if (clearBoxes) {
				const { layers } = mapRefs.current[oldPageNumber]
				layers.boundingBoxes.getSource()?.clear()
			}
		}, 150)
	})

	runInAction(() => {
		mapController.showBoundingBox = action(
			(boundingBox?: BoundingBox, pageNumber?: number, fitToField = false) => {
				if (mapRefs.current.length === 0) return

				const oldPageNumber = controller.currentPageNumber

				const oldPageMapAssets = mapRefs.current[oldPageNumber]
				oldPageMapAssets.layers.boundingBoxes.getSource()?.clear()

				controller.selectedBoundingBox = boundingBox

				if (
					boundingBox === undefined ||
					boundingBox.length === 0 ||
					pageNumber === undefined
				) {
					mapController.fitToPage?.call(null, controller.currentPageNumber)
					const { layers } = mapRefs.current[controller.currentPageNumber]
					layers.boundingBoxes.getSource()?.clear()
					return
				}

				const { map, layers } = mapRefs.current[pageNumber - 1]
				let extent = controller.documentRenderer?.getPage(
					pageNumber - 1,
				)?.extent

				if (extent === undefined) throw new Error('page extent was undefined')
				if (controller.ocrExtent === undefined)
					throw new Error('ocr extent was undefined')

				layers.boundingBoxes.getSource()?.clear()

				if (isBoundingBoxEmpty(boundingBox)) {
					mapController.fitToPage?.call(null, pageNumber - 1, true)
					return
				}

				const color = orange[800]

				layers.boundingBoxes.getSource()?.addFeature(
					createBoundingBoxFeature(
						{
							boundingBox: boundingBox,
							color: color,
							borderWidth: 2,
							fillColor: `${color}40`,
						},
						extent,
						controller.ocrExtent,
					),
				)

				extent = createExtent(boundingBox, imageExtent, controller.ocrExtent)

				const verticalRatio = imageExtent[3] / 5
				const horizontalRatio = imageExtent[2] / 5

				extent[0] = extent[0] - horizontalRatio
				extent[1] = extent[1] - verticalRatio
				extent[2] = extent[2] + horizontalRatio
				extent[3] = extent[3] + verticalRatio

				controller.currentPageNumber = pageNumber - 1

				console.log(controller.currentPageNumber)

				if (fitToField) map.getView().fit(extent)
				else if (mapController.fitToPage)
					mapController.fitToPage(controller.currentPageNumber)

				return
			},
		)
	})

	const getDefaultZoom = (
		extent: Extent,
		mapElement: HTMLDivElement,
	): number => {
		const isLandscape =
			controller.invoiceResults.documentFormat === DocumentFormat.Landscape

		return getZoom(
			extent,
			mapElement.clientHeight,
			mapElement.clientWidth,
			isLandscape ? 1000 : 50, // todo revisit when I get back to invoices
			isLandscape ? 1000 : 50,
		)
	}

	const getZoom = (
		extent: Extent,
		height: number,
		width: number,
		heightOffset: number,
		widthOffset: number,
	): number => {
		const mode = width > height ? 'landscape' : 'portrait'
		const containerAspectRatio =
			mode === 'landscape' ? width / height : height / width
		const imageAspectRatio =
			mode === 'landscape' ? extent[2] / extent[3] : extent[3] / extent[2]

		// if the image is wider than what fits in the container, fit to width
		if (imageAspectRatio > containerAspectRatio)
			return Math.LOG2E * Math.log((height - heightOffset) / 256)
		// fit to height
		else return Math.LOG2E * Math.log((width - widthOffset) / 256)
	}

	const handleZoomIn = () => {
		const currentRef = mapRefs.current[controller.currentPageNumber]
		if (currentRef === undefined) return
		const zoomLevel = currentRef.map.getView().getZoom()
		if (zoomLevel !== undefined)
			currentRef.map.getView().setZoom(zoomLevel + 0.3)
	}

	const handleZoomOut = () => {
		const currentRef = mapRefs.current[controller.currentPageNumber]
		if (currentRef === undefined) return
		const zoomLevel = currentRef.map.getView().getZoom()
		if (zoomLevel !== undefined)
			currentRef.map.getView().setZoom(zoomLevel - 0.3)
	}

	const handleBackClicked = () => {
		history.push(`/_administration/_tasks`)
	}

	if (documentRenderer === undefined) return <FullscreenSpinner />

	if (!controller.controllerReady) return <FullscreenSpinner />

	console.log('page images:', controller.documentRenderer?.pageImages.length)

	return (
		<div className={styles.documentViewer}>
			{!props.hideBackButton && (
				<>
					<IconButton
						onClick={handleBackClicked}
						color="primary"
						size="small"
						className={styles.backButton}
					>
						<ArrowBack />
					</IconButton>
				</>
			)}

			<div className={styles.mapControls}>
				<IconButton
					onClick={handleZoomOut}
					color="primary"
					size="small"
					className={styles.zoomIcon}
				>
					<ZoomOut />
				</IconButton>
				<IconButton
					onClick={handleZoomIn}
					color="primary"
					size="small"
					className={styles.zoomIcon}
				>
					<ZoomIn />
				</IconButton>
				<IconButton
					onClick={() =>
						controller.mapControl.fitToPage?.call(
							null,
							controller.currentPageNumber,
						)
					}
					color="primary"
					size="small"
				>
					<Home />
				</IconButton>
			</div>

			<div
				style={{
					height: '100%',
					width: '100%',
					position: 'relative',
				}}
			>
				{controller.documentRenderer?.pageImages.map((v, i) => (
					<InternalFileMap
						key={i}
						id={`map${i}`}
						imageData={v}
						layers={{
							// TODO - memoize?
							boundingBoxes: createLayer(),
							additionalLayers: [],
						}}
						visible={i === controller.currentPageNumber}
						onMapMounted={(map) => {
							mapRefs.current[i] = map
						}}
					/>
				))}
			</div>

			<IconButton
				onClick={() => {
					promptService.showDialog((props) => (
						<KeyboardShortcutDialog onClose={() => props.close('okay')} />
					))
				}}
				color="primary"
				size="small"
				className={styles.infoButton}
			>
				<Info />
			</IconButton>
		</div>
	)
})

type InternalFileMapProps = {
	id: string
	visible: boolean
	imageData: OcrImageData
	layers: FileMapLayers
	onMapMounted: (map: MapAssets) => void
}

const InternalFileMap = observer((props: InternalFileMapProps) => {
	const styles = useStyles()

	const mapRef = useRef<Map | null>()
	const mapHtmlElementRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const imageLayer = new ImageLayer()
		const annotationGroup = new LayerGroup()
		const collection = new Collection([
			props.layers.boundingBoxes,
			...props.layers.additionalLayers,
		])
		annotationGroup.setLayers(collection)

		imageLayer.setSource(
			new Static({
				url: props.imageData.dataUrl,
				projection: new Projection({
					code: `ocr-projection-${props.id}`,
					units: 'pixels',
					extent: props.imageData.extent,
				}),
				imageExtent: props.imageData.extent,
			}),
		)

		const map = new Map({
			target: props.id,
			controls: [],
			layers: [imageLayer, annotationGroup],
			view: new View({
				projection: new Projection({
					code: `ocr-projection-${props.id}`,
					units: 'pixels',
					extent: props.imageData.extent,
				}),
				center: getCenter(props.imageData.extent),
				zoom: getDefaultZoom(props.imageData.extent),
				maxZoom: 8,
			}),
		})
		mapRef.current = map

		if (mapHtmlElementRef.current === null)
			throw new Error('failed to bind to HTML div element')

		props.onMapMounted({
			map,
			layers: props.layers,
			htmlElement: mapHtmlElementRef.current,
		})

		return () => {
			map.dispose()
		}
	}, [props.layers, props.imageData, props.id, props.onMapMounted])

	// the maps were creating multiple viewports for each map but this solves it
	useEffect(() => {
		const htmlElement = mapHtmlElementRef.current
		if (!htmlElement) return
		const elementChildren = htmlElement.children
		if (elementChildren.length > 1) htmlElement.removeChild(elementChildren[0])
	}, [])

	const getDefaultZoom = (extent: Extent): number => {
		const mapElement = mapHtmlElementRef.current
		if (mapElement === null) throw Error('The map element ref is null')

		return getZoom(
			extent,
			mapElement.clientHeight,
			mapElement.clientWidth,
			1000, // todo revist when i get back to invoices
			1000,
		)
	}

	const getZoom = (
		extent: Extent,
		height: number,
		width: number,
		heightOffset: number,
		widthOffset: number,
	): number => {
		const mode = width > height ? 'landscape' : 'portrait'

		const containerAspectRatio =
			mode === 'landscape' ? width / height : height / width
		const imageAspectRatio =
			mode === 'landscape' ? extent[2] / extent[3] : extent[3] / extent[2]

		// if the image is wider than what fits in the container, fit to width
		if (imageAspectRatio > containerAspectRatio)
			return Math.LOG2E * Math.log((height - heightOffset) / 256)
		// fit to height
		else return Math.LOG2E * Math.log((width - widthOffset) / 256)
	}

	return (
		<div
			id={props.id}
			className={clsx(styles.innerMapWrapper, {
				[styles.selectedPage]: props.visible,
				[styles.hiddenPage]: !props.visible,
			})}
			ref={mapHtmlElementRef}
		>
			{' '}
		</div>
	)
})

const useStyles = makeStyles((theme: Theme) => ({
	outerMapWrapper: {
		height: percent(100),
		width: percent(100),
		position: 'relative',
	},

	innerMapWrapper: {
		position: 'absolute',
		display: 'flex',
		height: percent(100),
		width: percent(100),
	},

	documentViewer: {
		position: 'relative',
		flex: 14,
		// [theme.breakpoints.up('sm')]: { flex: '10' },
		// [theme.breakpoints.down('sm')]: { flex: '14' },
	},
	backButton: {
		marginTop: theme.spacing(2),
		marginLeft: theme.spacing(2),
		float: 'left',
		position: 'absolute',
		zIndex: theme.zIndex.speedDial,
	},

	zoomIcon: {
		marginRight: theme.spacing(1),
	},

	infoButton: {
		position: 'absolute',
		marginBottom: theme.spacing(2),
		marginRight: theme.spacing(2),
		bottom: 0,
		right: 0,
		zIndex: theme.zIndex.speedDial,
	},

	mapControls: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		position: 'absolute',
		zIndex: theme.zIndex.speedDial,
		marginLeft: 'auto',
		right: 0,
	},
	selectedPage: {
		zIndex: 1,
	},
	hiddenPage: {
		zIndex: -1,
	},
}))
